import { yupResolver } from '@hookform/resolvers/yup';
import startOfMonth from 'date-fns/startOfMonth';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { REPORT_MESSAGES } from '~/constants/messages.constants';
import { useAppSelector } from '~/hooks/useAppSelector';
import MeasurementReportService from '~/services/resources/measurementReport';
import {
  groupMeasurementByCategory,
  mapGroupedMeasurementToOptions,
} from '~/utils/activitymetrics';
import Yup from '~/utils/validations/yup';

import {
  ModalReportLogHoursDownloadFormValues,
  ModalReportLogHoursDownloadProps,
} from './types';

const Schema = Yup.object({
  format: Yup.string().required(),
  units: Yup.array(Yup.object({ label: Yup.string(), value: Yup.string() }))
    .min(1, 'At least one unit is required')
    .required(),
  startDate: Yup.date().required(),
  endDate: Yup.date().required(),
});

export const useModalReportLogHoursDownloadController = ({
  modalRef,
}: ModalReportLogHoursDownloadProps) => {
  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<ModalReportLogHoursDownloadFormValues>({
    defaultValues: {
      format: 'XLSX',
      units: [],
      startDate: startOfMonth(new Date()),
      endDate: new Date(),
    },
    resolver: yupResolver(Schema),
  });
  const { selectedEcosystem } = useAppSelector(({ ecosystem }) => ecosystem);

  const unitOptions = useMemo(() => {
    if (!selectedEcosystem?.measurementUnits) return [];
    const groupedByCategory = groupMeasurementByCategory(
      selectedEcosystem?.measurementUnits,
    );
    return mapGroupedMeasurementToOptions(groupedByCategory);
  }, [selectedEcosystem?.measurementUnits]);

  const handleCloseModal = modalRef.current?.close;

  const onSubmit = async (data: ModalReportLogHoursDownloadFormValues) => {
    if (data.format === 'CSV') {
      await handleGenerateReportCSV(data);
      return;
    }
    await handleGenerateReportXLSX(data);
  };

  const handleGenerateReportCSV = async (
    data: ModalReportLogHoursDownloadFormValues,
  ) => {
    try {
      if (!selectedEcosystem) return;
      const filter = {
        units: data.units.map((unit) => unit.value),
        startDate: data.startDate,
        endDate: data.endDate,
      };
      await MeasurementReportService.membersLogHoursDownloadReportCSV(
        selectedEcosystem?._id,
        filter,
      );
      reset();
      toast.success(REPORT_MESSAGES.REQUESTED);
    } catch (error) {
      toast.error(REPORT_MESSAGES.DOWNLOAD_ERROR);
    } finally {
      handleCloseModal?.();
    }
  };

  const handleGenerateReportXLSX = async (
    data: ModalReportLogHoursDownloadFormValues,
  ) => {
    try {
      if (!selectedEcosystem) return;
      const filter = {
        units: data.units.map((unit) => unit.value),
        startDate: data.startDate,
        endDate: data.endDate,
      };
      await MeasurementReportService.membersLogHoursDownloadReportXLSX(
        selectedEcosystem?._id,
        filter,
      );
      reset();
      toast.success(REPORT_MESSAGES.REQUESTED);
    } catch (error) {
      toast.error(REPORT_MESSAGES.DOWNLOAD_ERROR);
    } finally {
      handleCloseModal?.();
    }
  };

  return {
    errors,
    control,
    isSubmitting,
    unitOptions,
    onSubmit,
    handleCloseModal,
    handleSubmit,
  };
};
